import { useEffect } from "react";
import dynamic from "next/dynamic";
import Head from "next/head";
import Theme from "../../components/Theme";
import AppStorage from "../../components/AppStorage";
import PricingPlan from "../../components/organisms/common/PricingPlan";
import PlanComparision from "../../components/organisms/pricing/PlanComparision";
import PlanComparisionMobile from "../../components/organisms/pricing/PlanComparision-Mobile";
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../../styles/cssinjs/helpers/variables.js";
import { CONSTANTS } from "@util/utils";

const InjectChatSupport = dynamic(() =>
  import("@components/atoms/InjectChatSupport")
);
const Schema = dynamic(() => import("@components/organisms/common/Schema"));
const WideCTA = dynamic(() => import("@components/organisms/common/WideCTA"));
const CTA = dynamic(() => import("@components/organisms/common/CTA"));
const FAQ = dynamic(() => import("@components/organisms/common/FAQ"));
export async function getStaticProps(ctx) {
  const { locale } = ctx;
  let allData = {};

  // Fetch
  const headerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/header.json`
  );
  const footerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/footer.json`
  );
  const marketingResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/pricing/root/marketing.json`
  );
  const contentResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/pricing/root/content.json`
  );

  // Catch Error
  if (marketingResponse.status === 404 || contentResponse.status === 404) {
    return {
      notFound: true,
    };
  }

  // Convert
  allData.header = await headerResponse.json();
  allData.footer = await footerResponse.json();
  allData.marketing = await marketingResponse.json();
  allData.content = await contentResponse.json();

  // Wrap
  const header = {
    header: allData.header,
  };
  const footer = {
    footer: allData.footer,
  };

  // Combine all data
  const data = await {
    ...header,
    ...footer,
    ...allData.marketing,
    ...allData.content,
  };

  return {
    props: {
      data,
      fallback: false,
    },
  };
}

const url =
  process.env.APP_MODE === "live"
    ? CONSTANTS.live.universalSignupUrl
    : CONSTANTS.staging.universalSignupUrl;

const trackIds = [
  {
    element: "pro-cta-button",
    id: {
      monthly: "pro-hero-monthly",
      annual: "pro-hero-annually",
    },
    url: {
      monthly: `${url}/signup?planId=4982`,
      annual: `${url}/signup?planId=5189`,
    },
  },
  {
    element: "team-cta-button",
    id: {
      monthly: "team-hero-monthly",
      annual: "team-hero-annually",
    },
    url: {
      monthly: `${url}/signup?planId=4983`,
      annual: `${url}/signup?planId=4985`,
    },
  },
  {
    element: "pro-cta-footer",
    id: {
      monthly: "pro-pre-footer-monthly",
      annual: "pro-pre-footer-annually",
    },
    url: {
      monthly: `${url}/signup?planId=4982`,
      annual: `${url}/signup?planId=5189`,
    },
  },
  {
    element: "team-cta-footer",
    id: {
      monthly: "team-pre-footer-monthly",
      annual: "team-pre-footer-annually",
    },
    url: {
      monthly: `${url}/signup?planId=4983`,
      annual: `${url}/signup?planId=4985`,
    },
  },
  {
    element: "pro-cta-mobile-footer",
    id: {
      monthly: "pro-pre-footer-monthly",
      annual: "pro-pre-footer-annually",
    },
    url: {
      monthly: `${url}/signup?planId=4982`,
      annual: `${url}/signup?planId=5189`,
    },
  },
  {
    element: "team-cta-mobile-footer",
    id: {
      monthly: "team-pre-footer-monthly",
      annual: "team-pre-footer-annually",
    },
    url: {
      monthly: `${url}/signup?planId=4983`,
      annual: `${url}/signup?planId=4985`,
    },
  },
];

export default function Index({ data, nonce }) {
  const seo = data.seo;
  const header = data.header;
  const footer = data.footer;
  const content = data.content;

  useEffect(() => {
    window.trackIds = trackIds;
    // togglePlan();
  });

  const initialPlanType = (() => {
    if (content.initialPlanType === "monthly-billing-plan") {
      return "annual-billing-plan";
    } else {
      return "monthly-billing-plan";
    }
  })();

  const PricingPlanWithTitleDesc = () => (
    <section
      className="pricing-wrap has-align-top lg:pb-10 lg:pt-10 pricing-container"
      id={initialPlanType}
    >
      <article className="text-center mx-auto md:pt-10 md:px-3">
        <h1
          className="text-xl lg:pt-5 lg:mb-3 md:mt-2 md:text-left"
          dangerouslySetInnerHTML={{ __html: content.pricingPlan.heading }}
        />
        <p
          className="text-sm lg:mb-5 md:text-left"
          dangerouslySetInnerHTML={{ __html: content.pricingPlan.desc }}
        />
      </article>
      <PricingPlan
        toggle={content.pricingPlan.toggle}
        plans={content.pricingPlan.plans}
        planInfo={content.pricingPlan.planInfo}
        trackIds={trackIds}
        contactCard={content.contactSection}
        pageName="Pricing Root Page"
      />
      <style jsx>{`
        .pricing-wrap {
          background-color: ${colors.primitive.grey[600]};
        }
      `}</style>
    </section>
  );
  return (
    <>
      <Head>
        <meta
          name="apple-itunes-app"
          content="app-id=665859578, app-argument=https://apps.apple.com/us/app/setmore-appointment-scheduling/id665859578?pid=ios_smart_banner&c=ios_campaign"
        />
      </Head>
      <Theme
        header={header}
        footer={footer}
        seo={seo}
        nonce={nonce}
        className="pricing-layout pricing_active"
      >
        <AppStorage pageName="Pricing Root Page" />
        <PricingPlanWithTitleDesc />
        <PlanComparision
          heading={content.pricingComparison.heading}
          categories={content.pricingComparison.categories}
          planCost={content.pricingComparison.planCost}
          ctaButtons={content.pricingComparison.ctaButtons}
          trackIds={trackIds}
        />
        <PlanComparisionMobile
          heading={content.pricingComparison.heading}
          categories={content.pricingComparison.categories}
          planCost={content.pricingComparison.planCost}
          ctaButtons={content.pricingComparison.ctaButtons}
          trackIds={trackIds}
        />
        <CTA
          className="lg:mt-8 lg:mb-10 md:mt-10 md:mb-10 bg-offset-top"
          data={content.cta}
          wrapperClass="cta-article"
          ctaBgDarkGreenColor={true}
        />
        <FAQ
          className="lg:pb-20 lg:pt-10 bg-light-blue-grey  md:pb-10"
          data={content.faqs}
        />
        <WideCTA data={content.wideCTA} />
        <InjectChatSupport />
        <AppStorage pageName="Pricing" lang="EN" />
      </Theme>
      <Schema data={content.faqs.list} />
      <style jsx global>{`
        .chatsupport-button {
          visibility: visible !important;
        }
        .cta-contrast.has-noimage article {
          max-width: 760px;
        }
        .bg-offset-top::before {
          top: 80px;
          background-color: ${colors.primitive.grey[600]} !important;
        }
        .cta-article {
          max-width: 450px;
        }
        @media (min-width: ${viewport.tablet.sm}) {
          .pr-card .abs--img-card {
            top: 92px;
            left: 84px;
          }
        }
        @media (max-width: ${viewport.tablet.sm}) {
          .bg-offset-top::before {
            height: 320px;
            top: 40px;
          }
          .faq-section h2 {
            font-size: ${font.size.lg[4]};
            line-height: ${lineHeight[16]};
            margin-bottom: 40px !important;
          }
        }
      `}</style>
    </>
  );
}
